import React, { useState, useEffect } from "react";
import { IntlProvider } from "react-intl";
import LanguageContext from "./Context";
import { LANG, CATEGORY, COLLECTION } from "config/const";
import { useShop } from "providers/Shop";
import { useLiff } from "providers/Liff";
import { useAuth } from "providers/Auth";
import localLocales from "config/locales";

import * as Client from "api/client";

const I18nProvider = ({ children }) => {
  const { liff, ready } = useLiff();
  const [lang, setLang] = useState(LANG.TH);
  const [messages, setMessages] = useState({});
  const [i18nEntities, setI18nEntities] = useState([]);
  const { shop } = useShop();

  const { auth } = useAuth();

  const getShopI18nEntities = () =>
    Client.getShopI18nEntities(auth.idToken).then((res) => {
      if (!res.err) {
        return res;
      } else {
        return false;
      }
    });

  useEffect(() => {
    const storedLang = localStorage.getItem("lang");
    if (liff && ready) {
      // 從 API 取得 i18n entities
      getShopI18nEntities()
        .then((res) => {
          if (res) setI18nEntities(res);
        })
        .catch((err) => {
          console.error(err);
        });

      const liffLang = liff.getLanguage();
      //如果不是泰文就預設英文
      const supportedLang =
        liffLang.split("-")[0] === LANG.TH ? LANG.TH : LANG.EN;

      setLang(storedLang || supportedLang || LANG.TH);
    } else {
      setLang(storedLang || LANG.TH);
    }
  }, [liff, ready]);

  useEffect(() => {
    const loadMessages = async () => {
      try {
        const mergedByLocale = i18nEntities.reduce((acc, item) => {
          Object.entries(item.locale).forEach(([langKey, langMessages]) => {
            if (!acc[langKey]) acc[langKey] = {};

            // format CATEGORY、COLLECTION 以符合 {entityType}_{entityId}_{key} 結構
            if ([CATEGORY, COLLECTION].includes(item.entityType)) {
              const prefix = `${item.entityType}_${item.entityId}`;
              Object.entries(langMessages).forEach(([key, value]) => {
                acc[langKey][`${prefix}_${key}`] = value;
              });
            } else {
              // 其他 entityType (例如 LANDING_PAGE) 直接 merge
              acc[langKey] = {
                ...acc[langKey],
                ...langMessages,
              };
            }
          });
          return acc;
        }, {});

        const loadedLocales = await Promise.all(
          localLocales.map(async (l) => ({
            locale: l.locale,
            messages: (await l.messages).default,
          }))
        );

        const finalLocales = loadedLocales.reduce((acc, l) => {
          acc[l.locale] = {
            ...l.messages,
            ...(mergedByLocale[l.locale] || {}),
          };
          return acc;
        }, {});

        const mergedMessages = finalLocales[lang] || {};
        setMessages(mergedMessages);
      } catch (error) {
        console.error(`Failed to load messages for ${lang}:`, error);
      }
    };

    loadMessages();
  }, [lang, shop, i18nEntities]);

  useEffect(() => {
    document.documentElement.lang = lang; // 設定 html 的 lang 屬性
    localStorage.setItem("lang", lang);
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang }}>
      <IntlProvider locale={lang} messages={messages}>
        {children}
      </IntlProvider>
    </LanguageContext.Provider>
  );
};

export default I18nProvider;
